import { z } from 'zod';
import {
  DataSyncExcelRefusalSchema,
  DataSyncExcelSheetSchema,
  type DataSyncExcelSheet,
  type StartExcelDataSyncRequest
} from '@bliks-io/contracts';
import i18next from '@/lib/i18n';
import { getSheetName, parseDataSyncWorkbook, readWorkbook, type ParsedRow } from '@/lib/excel';
import { getApiErrorCode } from '@/api/errors';

/** Longer lists would push the people table below the fold. */
const MAX_LISTED_INVALID_ROWS = 10;

/** Every upload refusal names a sheet and its offending rows in its metadata. */
const ExcelRefusalSchema = z.object({ metadata: DataSyncExcelRefusalSchema });

/** Why an upload stopped, with the offending rows when there are any. */
export interface UploadProblem {
  message: string;
  rows: string[];
}

/** Per sheet, its name as the file has it and the sheet row of each request row, in order. */
export type UploadSheets = Record<DataSyncExcelSheet, { name: string; rows: number[] }>;

export interface ExcelUpload {
  request: StartExcelDataSyncRequest;
  sheets: UploadSheets;
}

const rowNumbers = (rows: ParsedRow<unknown>[]) => rows.map(({ rowNumber }) => rowNumber);

/**
 * Read an uploaded workbook into the upload request, or the problem that
 * stops the upload: missing sheets (a file of another layout), invalid rows,
 * or no people at all (an empty upload would propose everyone as a leaver).
 * Problems name the sheets as the file has them. Rejects when the file
 * cannot be read.
 */
export async function readExcelUpload(file: File): Promise<ExcelUpload | UploadProblem> {
  const parsed = parseDataSyncWorkbook(await readWorkbook(file));
  if (parsed.kind === 'missingSheets') {
    return {
      message: i18next.t('dataSync.settings.missingSheets', {
        sheets: parsed.dataAreas.map(getSheetName).join(', ')
      }),
      rows: []
    };
  }
  if (parsed.kind === 'missingColumns') {
    return {
      message: i18next.t('dataSync.settings.missingColumns'),
      rows: parsed.sheets.map(({ name, columns }) =>
        i18next.t('dataSync.settings.sheetColumns', {
          sheet: name,
          columns: columns.map(({ translationKey }) => i18next.t(translationKey)).join(', ')
        })
      )
    };
  }
  const { sheets } = parsed;
  const invalid = DataSyncExcelSheetSchema.options.flatMap((sheet) => {
    const rows: ParsedRow<unknown>[] = sheets[sheet].rows;
    return rows.filter((row) => !row.isValid).map((row) => ({ sheet, row }));
  });
  if (invalid.length > 0) {
    return {
      message: i18next.t('initialSetup.upload.invalidRowsCount', { count: invalid.length }),
      rows: invalid.slice(0, MAX_LISTED_INVALID_ROWS).map(({ sheet, row }) =>
        i18next.t('dataSync.settings.invalidRow', {
          sheet: sheets[sheet].name,
          row: row.rowNumber,
          errors: (row.errors ?? []).map(({ message }) => message).join(', ')
        })
      )
    };
  }
  if (sheets.people.rows.length === 0) {
    return {
      message: i18next.t('dataSync.settings.emptyFile', { sheet: sheets.people.name }),
      rows: []
    };
  }
  const dataOf = <T>({ rows }: { rows: ParsedRow<T>[] }) => rows.map(({ data }) => data);
  const uploadSheet = (sheet: DataSyncExcelSheet) => ({
    name: sheets[sheet].name,
    rows: rowNumbers(sheets[sheet].rows)
  });
  return {
    request: {
      departments: dataOf(sheets.departments),
      teams: dataOf(sheets.teams),
      roles: dataOf(sheets.roles),
      systems: dataOf(sheets.systems),
      people: sheets.people.rows.map(({ data }) => ({
        ...data,
        department: data.department || null,
        role: data.role || null,
        location: data.location || null
      })),
      ...(parsed.downloaded && { downloaded: parsed.downloaded })
    },
    sheets: {
      departments: uploadSheet('departments'),
      teams: uploadSheet('teams'),
      roles: uploadSheet('roles'),
      systems: uploadSheet('systems'),
      people: uploadSheet('people')
    }
  };
}

/**
 * The backend's refusal of an upload, recognized by the sheet and rows it
 * names, with those rows mapped back to their sheet and the sheets named as
 * the file has them; null for any other error.
 */
export function refusalProblem(error: unknown, sheets: UploadSheets): UploadProblem | null {
  const code = getApiErrorCode(error);
  const refusal = ExcelRefusalSchema.safeParse(error);
  if (!code || !refusal.success) return null;
  const { sheet, indices } = refusal.data.metadata;
  const rows = indices.map((index) => sheets[sheet].rows[Number(index)]).join(', ');
  return {
    message: i18next.t(code, {
      rows: i18next.t('dataSync.settings.sheetRows', {
        sheet: sheets[sheet].name,
        rows,
        count: indices.length
      }),
      sheet: sheets[sheet].name,
      departmentsSheet: sheets.departments.name,
      rolesSheet: sheets.roles.name,
      peopleSheet: sheets.people.name
    }),
    rows: []
  };
}
